import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FORCAS_DIAGNOSTICO } from './index.js';
import { api } from '@/lib/api.js';

type Props = {
  diagnosticoId: string;
  initialAnswers?: (number | null)[];
  onComplete: () => void | Promise<void>;
  onCancel?: () => void;
};

const ROTULOS = ['Nunca', 'Raramente', 'Às vezes', 'Frequente', 'Sempre'];

export function DiagnosticoFlow({ diagnosticoId, initialAnswers, onComplete, onCancel }: Props) {
  const [fi, setFi] = useState(0);
  const [ti, setTi] = useState(0);
  const [qi, setQi] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>(
    () => initialAnswers ?? Array(50).fill(null),
  );
  const [saving, setSaving] = useState(false);
  const prevThemeKey = useRef<string | null>(null);

  useEffect(() => {
    if (!initialAnswers) return;
    setAnswers(initialAnswers);
    const idx = initialAnswers.findIndex((a) => a === null);
    if (idx < 0) return;
    setFi(Math.floor(idx / 10));
    setTi(Math.floor((idx % 10) / 5));
    setQi(idx % 5);
  }, [initialAnswers]);

  const g = fi * 10 + ti * 5 + qi;
  const f = FORCAS_DIAGNOSTICO[fi]!;
  const tema = f.temas[ti]!;
  const qtext = tema.qs[qi]!;
  const current = answers[g] ?? null;
  const pct = Math.round((g / 50) * 100);

  const savePartial = useCallback(
    async (payload: (number | null)[], concluir: boolean) => {
      setSaving(true);
      try {
        await api.post(`/diagnosticos/${diagnosticoId}/resposta`, {
          respostas: payload,
          concluir,
        });
      } finally {
        setSaving(false);
      }
    },
    [diagnosticoId],
  );

  const themeKey = `${fi}-${ti}`;
  useEffect(() => {
    if (prevThemeKey.current === null) {
      prevThemeKey.current = themeKey;
      return;
    }
    if (prevThemeKey.current === themeKey) return;
    prevThemeKey.current = themeKey;
    const hasAny = answers.some((a) => a !== null);
    if (hasAny) void savePartial(answers, false);
  }, [themeKey, answers, savePartial]);

  const select = useCallback(
    (v: number) => {
      setAnswers((prev) => {
        const n = [...prev];
        n[g] = v;
        return n;
      });
    },
    [g],
  );

  const navNext = useCallback(async () => {
    if (current === null) return;
    const nextAnswers = [...answers];
    nextAnswers[g] = current;
    setAnswers(nextAnswers);

    if (fi === 4 && ti === 1 && qi === 4) {
      if (!nextAnswers.every((a) => a !== null && a >= 1 && a <= 5)) return;
      await savePartial(nextAnswers, true);
      await onComplete();
      return;
    }
    if (qi < 4) setQi((q) => q + 1);
    else if (ti < 1) {
      setTi(1);
      setQi(0);
    } else {
      setFi((x) => x + 1);
      setTi(0);
      setQi(0);
    }
  }, [answers, current, fi, g, onComplete, qi, savePartial, ti]);

  const navBack = useCallback(() => {
    if (qi > 0) setQi((q) => q - 1);
    else if (ti > 0) {
      setTi(0);
      setQi(4);
    } else if (fi > 0) {
      setFi((x) => x - 1);
      setTi(1);
      setQi(4);
    }
  }, [fi, qi, ti]);

  const last = fi === 4 && ti === 1 && qi === 4;

  const headerBlock = useMemo(() => {
    if (ti === 0 && qi === 0) {
      return (
        <div className="border-l-4 px-5 py-7 md:px-9 md:py-8" style={{ borderColor: f.hex, background: 'var(--bg2)' }}>
          <div className="text-[56px] font-black leading-none opacity-[0.12]" style={{ color: f.texto }}>
            {f.num}
          </div>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider" style={{ color: f.texto }}>
            {f.icone} {f.nome}
          </div>
          <div className="text-lg font-black leading-tight md:text-3xl">
            {f.nome} · {f.temas[0]!.nome} & {f.temas[1]!.nome}
          </div>
          <p className="mt-2 text-[13px] leading-relaxed text-ha-subtle">{f.desc}</p>
        </div>
      );
    }
    return null;
  }, [f, qi, ti]);

  const temaSep =
    qi === 0 ? (
      <div
        className="flex items-center gap-2.5 border-t border-ha-border px-5 py-3 md:px-9"
        style={{ borderLeftWidth: 4, borderLeftColor: f.hex, background: 'var(--bg3)' }}
      >
        <span
          className="whitespace-nowrap px-2 py-0.5 text-[8px] font-extrabold uppercase tracking-[3px]"
          style={{ color: f.texto, background: 'var(--bg2)' }}
        >
          Tema {ti === 0 ? 'A' : 'B'}
        </span>
        <span className="text-[13px] font-bold">{tema.nome}</span>
      </div>
    ) : null;

  return (
    <div className="mx-auto max-w-[820px] pb-24">
      <div className="sticky top-0 z-10 border-b border-ha-border bg-ha-bg px-5 py-3 md:px-8">
        <div className="mb-1.5 flex justify-between text-[11px] font-semibold uppercase tracking-wide">
          <span style={{ color: f.texto }}>
            {f.num} · {f.nome} / {tema.nome}
          </span>
          <span className="font-semibold text-ha-subtle">
            {g + 1} / 50 {saving ? '· salvando…' : ''}
          </span>
        </div>
        <div className="h-[3px] bg-ha-border">
          <div className="h-full transition-[width]" style={{ width: `${pct}%`, background: f.hex }} />
        </div>
      </div>

      {headerBlock}
      {temaSep}

      <div className="border-l-4 bg-ha-card2 px-5 py-7 md:px-9 md:py-8" style={{ borderColor: f.hex }}>
        <div className="mb-2.5 text-[11px] font-semibold uppercase tracking-wide text-ha-muted">
          Pergunta {g + 1} de 50 · {f.nome} / {tema.nome}
        </div>
        <p className="mb-6 text-base font-semibold leading-relaxed">{qtext}</p>
        <div className="grid grid-cols-5 gap-0.5">
          {([1, 2, 3, 4, 5] as const).map((v) => {
            const sel = current === v;
            return (
              <button
                key={v}
                type="button"
                onClick={() => select(v)}
                className="flex min-h-[60px] flex-col items-center justify-center gap-1 border border-ha-border2 bg-ha-card py-3 text-ha-subtle transition-colors"
                style={
                  sel
                    ? { background: f.hex, borderColor: f.hex, color: '#000' }
                    : undefined
                }
              >
                <span className="text-[21px] font-black">{v}</span>
                <span className="max-[360px]:hidden text-[8px] uppercase opacity-70">{ROTULOS[v - 1]}</span>
              </button>
            );
          })}
        </div>
        <div className="mt-1 flex justify-between text-[11px] text-ha-muted">
          <span>Não acontece</span>
          <span>Acontece sempre</span>
        </div>
      </div>

      <div className="sticky bottom-0 z-10 flex items-center justify-between gap-2.5 border-t border-ha-border bg-ha-card px-5 py-3.5 md:px-8">
        <button
          type="button"
          className="min-w-[80px] border border-ha-border2 bg-transparent px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-ha-subtle"
          style={{ visibility: fi === 0 && ti === 0 && qi === 0 ? 'hidden' : 'visible' }}
          onClick={navBack}
        >
          ← Voltar
        </button>
        {onCancel && (
          <button type="button" className="text-xs text-ha-muted underline" onClick={onCancel}>
            Sair
          </button>
        )}
        <button
          type="button"
          disabled={current === null || saving}
          className="max-w-[240px] flex-1 py-3 text-[11px] font-semibold uppercase tracking-wide text-black disabled:opacity-20"
          style={{
            background: 'linear-gradient(135deg,#ffc800 0%,#ffe47a 55%,#e6a800 100%)',
          }}
          onClick={() => void navNext()}
        >
          {last ? 'Ver resultado →' : 'Próxima →'}
        </button>
      </div>
    </div>
  );
}
